import { ReactNode, isValidElement } from 'react';
import { View } from 'react-native';
import { Stack } from '@/common/components/Stack/Stack';
import { tv } from '@/common/theme/tv.config';
import { TGap } from '@/common/theme/variants';
import { vars } from 'nativewind';
import {
  childMarginVariants,
  gridMarginVars,
  rootMarginVariants,
} from './Grid.utils';

export function Grid({
  gap,
  rowGap = gap,
  columnGap = gap,
  columnBasis,
  className,
  children,
  maxColumns,
  hideExtraCells,
}: TGridProps) {
  const rootMargin = rootMarginVariants(rowGap, columnGap) ?? '';
  const childMargin = childMarginVariants(rowGap, columnGap) ?? '';
  const rootStyle = tv({
    base: rootMargin,
  });
  const childStyle = tv({
    base: `grow ${columnBasis} ${childMargin}`,
  });

  const childrenList = Array.isArray(children)
    ? children.filter(isValidElement)
    : [children];

  const columns = maxColumns ? Math.max(maxColumns, 2) : 2;
  const extraCells = !hideExtraCells
    ? (columns - (childrenList.length % columns)) % columns
    : 0;

  return (
    <Stack
      wrap
      horizontal
      style={vars(gridMarginVars)}
      className={rootStyle({ className })}
    >
      {childrenList.map((child, idx) => (
        <View key={idx} className={childStyle()}>
          {child}
        </View>
      ))}
      {Array.from({ length: extraCells }).map((_, idx) => (
        <View key={idx} className={childStyle({ className: 'my-0 h-0' })} />
      ))}
    </Stack>
  );
}

export type TGridProps = {
  gap?: TGap;
  rowGap?: TGap;
  columnGap?: TGap;
  className?: string;
  children?: ReactNode;
  columnBasis?: `basis-${string}`;
  maxColumns?: number;
  hideExtraCells?: boolean;
};
